import { useState } from "react";
import styled from "styled-components";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Account, Manufacturer, Organization } from "@xfrontend/xicon";
import PanelProvider from "#/modules/common/PanelProvider";
import PageLoading from "#/modules/common/PageLoading";
import { useRbac } from "#/modules/auth/useRbac";
import UsersTab from "./UsersTab";
import OrganizationsTab from "./OrganizationsTab";

type TabKey = "accounts" | "organizations";

// Title + tabs share one row (matches xcockpit's Management TitleWrap).
const Head = styled.div`
  display: flex; align-items: center; gap: 18px; flex-wrap: wrap; margin-bottom: 18px;
`;
const Title = styled.h4`
  font-size: 24px; font-weight: bold; color: var(--x-gray-200, #f6f6f7); margin: 0;
`;
const TabBar = styled.div`display: flex; gap: 6px;`;

// xcockpit nav-pill: 32px pill, purple border, active filled purple-400, hover purple-500.
const TabBtn = styled.button<{ $active: boolean }>`
  display: inline-flex; align-items: center; gap: 6px;
  height: 32px; padding: 8px 11px; line-height: 14px; font-size: 14px;
  border-radius: 4px; cursor: pointer;
  border: 1px solid var(--x-purple-500, #494358);
  color: ${(p: { $active: boolean }) => (p.$active ? "var(--x-gray-200, #f6f6f7)" : "var(--x-gray-400, #cfd0d3)")};
  background: ${(p: { $active: boolean }) => (p.$active ? "var(--x-purple-400, #6a6280)" : "transparent")};
  &:hover { color: var(--x-gray-200, #f6f6f7); background: var(--x-purple-500, #494358); }
`;

// Disabled placeholder tab (Audit Log — no API yet): greyed, no border, not clickable.
const TabSoon = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  height: 32px; padding: 8px 11px; line-height: 14px; font-size: 14px;
  border-radius: 4px; cursor: not-allowed;
  border: 1px solid transparent; background: transparent;
  color: var(--x-gray-600, #6f7076);
`;

const iconColor = (active: boolean): string => (active ? "var(--x-gray-200, #f6f6f7)" : "var(--x-gray-400, #cfd0d3)");

// User-RBAC management page. UI term Organization (= internal LICENSE) / Territory (= CUSTOMER).
// Wrapped in PanelProvider so it shares the Dashboard's panel layering.
const Management = (): JSX.Element | null => {
  const { t } = useTranslation("management");
  const rbac = useRbac();
  const [tab, setTab] = useState<TabKey>("accounts");

  // Wait for /me roles before deciding — Cognito loads them async, so redirecting on
  // !isSystemAdmin too early would bounce a legitimate SYSTEM_ADMIN.
  if (!rbac.rolesLoaded) {
    return <PageLoading />;
  }
  // SYSTEM_ADMIN-only page (the license/management mutating APIs are SA-only). Defense-in-depth
  // behind the hidden menu item: anyone else who types the URL is redirected away.
  if (!rbac.isSystemAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <PanelProvider>
      <div style={{ padding: "12px" }}>
        <Head>
          <Title>{t("title")}</Title>
          <TabBar>
            <TabBtn type="button" $active={tab === "accounts"} onClick={() => setTab("accounts")}>
              <Account size={14} color={iconColor(tab === "accounts")} />
              {t("tabs.accounts")}
            </TabBtn>
            <TabBtn type="button" $active={tab === "organizations"} onClick={() => setTab("organizations")}>
              <Manufacturer size={14} color={iconColor(tab === "organizations")} />
              {t("tabs.organizations")}
            </TabBtn>
            <TabSoon type="button" disabled>
              <Organization size={14} color="var(--x-gray-600, #6f7076)" />
              {t("tabs.auditLog")}
            </TabSoon>
          </TabBar>
        </Head>
        {tab === "accounts" && <UsersTab rbac={rbac} />}
        {tab === "organizations" && <OrganizationsTab rbac={rbac} />}
      </div>
    </PanelProvider>
  );
};

export default Management;
